import { useEffect, useRef, useState } from 'react';
import {
	RetweetOutlined,
	DeleteOutlined,
	UndoOutlined,
	CloseOutlined,
	SaveOutlined,
	CloseCircleOutlined,
} from '@ant-design/icons';
import HydraulicSimulatorService from '../../domain/HydraulicSimulator/HydraulicSimulatorService';
import { Button, ComboboxLegacy as Combobox, Tooltip, Input } from '@hydrodigital/design-system';

const hydraulicSimulatorService = new HydraulicSimulatorService();

export default function Popup({
	mmsId,
	object,
	onDelete,
	onRestore,
	onUpdateAttributeValue,
	setAttributesValue,
	locationId,
	onClose,
	onMouseDown,
	isDragging,
	isActive = true,
	onClick,
	showDepthToggle = false,
	onShowDepthChange,
}) {
	// Local state for attributes (decoupled from object prop reactivity)
	const [localAttributes, setLocalAttributes] = useState(object?.attributes || []);
	// Local state to accumulate temporary changes
	const [pendingChanges, setPendingChanges] = useState({});
	const [hasChanges, setHasChanges] = useState(false);
	const [showDepth, setShowDepth] = useState(false);
	// ReadOnly / Edit mode toggle
	const [isEditing, setIsEditing] = useState(false);
	const containerRef = useRef(null);

	const handleGetAttributes = async () => {
		const response = await hydraulicSimulatorService.getAttributesFromAsset(
			mmsId,
			object.assetId,
			object.type,
			locationId,
		);
		setLocalAttributes(response.attributes);
		setAttributesValue(response.attributes);
	};

	// When the selected asset changes, sync localAttributes from object and reset changes
	useEffect(() => {
		setPendingChanges({});
		setHasChanges(false);
		setIsEditing(false);
		if (object?.attributes && object.attributes.length > 0) {
			setLocalAttributes(object.attributes);
		} else if (object?.assetId) {
			handleGetAttributes();
		}
	}, [object?.assetId]);

	// Click-outside: switch back to readOnly without closing
	useEffect(() => {
		const handleClickOutside = (e) => {
			if (containerRef.current && !containerRef.current.contains(e.target)) {
				setIsEditing(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Function to update temporary value (does not save immediately)
	const handleTempAttributeChange = (attributeName, newValue) => {
		setPendingChanges((prev) => ({ ...prev, [attributeName]: newValue }));
		setHasChanges(true);
	};

	// Function to save all changes
	const handleSaveChanges = () => {
		Object.entries(pendingChanges).forEach(([attributeName, newValue]) => {
			onUpdateAttributeValue(attributeName, newValue);
		});
		setPendingChanges({});
		setHasChanges(false);
		// Close popup after saving
		onClose();
	};

	// Function to cancel changes
	const handleCancelChanges = () => {
		setPendingChanges({});
		setHasChanges(false);
	};

	// Function to get the current value (with pending changes or original value)
	const getCurrentValue = (attribute) => {
		const hasPendingChange = pendingChanges.hasOwnProperty(attribute.name);
		let value = hasPendingChange ? pendingChanges[attribute.name] : attribute.value;
		
		if (attribute.numeric) {
			if (value === null || value === undefined || value === '' ||
			    (typeof value === 'number' && isNaN(value)) ||
			    (typeof value === 'string' && value.toLowerCase() === 'nan')) {
				value = '';
			} else {
				const numValue = parseFloat(value);
				value = isNaN(numValue) ? '' : String(numValue);
			}
		}
		return value;
	};

	return (
		<div
			ref={containerRef}
			className="flex flex-col"
			style={{
				width: '280px',
				borderRadius: '12px',
				color: '#29323c',
				padding: '12px 15px',
				fontSize: '11px',
				background: '#fff',
			}}
			onClick={(e) => {
				e.stopPropagation();
				if (!isEditing) setIsEditing(true);
				if (onClick) onClick(e);
			}}
		>
			{/* ── Header: assetId centrado + delete/restore + close ── */}
			<div
				className="flex items-center justify-between pb-2 mb-1 border-b-[1px] border-b-[#e9eaea]"
				style={{
					cursor: onMouseDown ? (isDragging ? 'grabbing' : 'grab') : 'default',
					userSelect: 'none',
				}}
				onMouseDown={onMouseDown}
			>
				<div className="flex items-center gap-2" style={{ width: 24 }}>
					{/* {isActive && !object.invisible && onDelete && (
						<DeleteOutlined
							style={{ color: '#ff4d4f', fontSize: '14px', cursor: 'pointer' }}
							onClick={(e) => { e.stopPropagation(); onDelete(); }}
						/>
					)}
					{isActive && object.invisible && onRestore && (
						<UndoOutlined
							style={{ color: '#52c41a', fontSize: '14px', cursor: 'pointer' }}
							onClick={(e) => { e.stopPropagation(); onRestore(); }}
						/>
					)} */}
				</div>

				<span style={{ fontWeight: 'bold', fontSize: '13px', textAlign: 'center', flex: 1 }}>
					{object.assetId || ''}
				</span>

				{onClose && (
					<div
						style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
						onClick={(e) => { e.stopPropagation(); onClose(); }}
					>
						<CloseOutlined style={{ color: '#74788d', fontSize: '10px' }} />
					</div>
				)}
			</div>

			{/* ── Type subtitle ── */}
			{object.type && (
				<div className="text-center pb-2 mb-1 border-b-[1px] border-b-[#e9eaea]">
					<span style={{ fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
						{object.type}
					</span>
				</div>
			)}

			{/* ── Attributes table ── */}
			{localAttributes && localAttributes.length > 0 ? (
				<div className="flex flex-col w-full mt-1">
					<div className="flex w-full mb-1 pb-1 border-b-[1px] border-b-[#e9eaea]">
						<div className="w-2/4 text-xs font-medium">Parameter</div>
						<div className="w-2/4 text-xs text-gray-400">Value</div>
					</div>
					<div style={{ maxHeight: '256px', overflowY: 'auto', overflowX: 'hidden' }}>
					{localAttributes.map((at, index) => {
						const isReadOnly = !isActive || !isEditing || !at.editable;
						const unit = at.unit && at.unit !== '-' && at.unit !== null ? ` ${at.unit}` : '';
						return (
							<div
								key={`${at.name}-${index}`}
								className="flex w-full justify-between mb-1 pb-1 border-b-[1px] border-b-[#e9eaea]"
							>
								<div className="w-2/4 pr-2">
									<Tooltip title={at.description || ''} minWidth={300}>
										<div className="text-xs capitalize flex items-center" style={{ lineHeight: '1.3' }}>
											{at.alias || at.name}
										</div>
									</Tooltip>
								</div>
								<div className="w-2/4">
									{isReadOnly ? (
										<span className="text-xs" style={{ color: isActive ? '#29323c' : '#9ca3af' }}>
											{at.value}{unit}
										</span>
									) : at.options && at.options.length > 0 ? (
										<Combobox
											value={getCurrentValue(at)}
											onChange={(val) => handleTempAttributeChange(at.name, val.value)}
											style={{ width: '100%', fontSize: '11px' }}
											options={(at.options || []).map((i) => ({ value: i, label: i }))}
											size="small"
										/>
									) : at.numeric ? (
										<Input
											value={getCurrentValue(at)}
											style={{ width: '100%', fontSize: '11px' }}
											size='small'
											placeholder=""
											type="number"
											min={at.ranges?.[0]}
											max={at.ranges?.[1]}
											step={0.1}
											onChange={(ev) => handleTempAttributeChange(at.name, ev.target.value)}
											onBlur={(ev) => {
												if (ev.target.value === '') {
													handleTempAttributeChange(at.name, at.ranges?.[0] || 0);
												}
											}}
										/>
									) : (
										<Input
											value={getCurrentValue(at)}
											style={{ width: '100%', fontSize: '11px' }}
											placeholder=""
											size="small"
											onChange={(ev) => handleTempAttributeChange(at.name, ev.target.value)}
										/>
									)}
								</div>
							</div>
						);
					})}
					</div>
				</div>
			) : (
				<div className="flex w-full items-center justify-center py-4">
					<span className="text-xs text-gray-400">Loading attributes...</span>
				</div>
			)}

			{/* ── Show Depth checkbox ── */}
			{/* {showDepthToggle && (
				<div className="flex items-center gap-2 mt-2 pt-2 border-t-[1px] border-t-[#e9eaea]">
					<input
						type="checkbox"
						id="show-depth-cb"
						checked={showDepth}
						onChange={(e) => {
							setShowDepth(e.target.checked);
							if (onShowDepthChange) onShowDepthChange(e.target.checked);
						}}
						style={{ cursor: 'pointer' }}
					/>
					<label htmlFor="show-depth-cb" className="text-xs" style={{ cursor: 'pointer', userSelect: 'none' }}>
						Show Depth
					</label>
				</div>
			)} */}

			{/* ── Footer: Clear + Save ── */}
			{isActive && isEditing && (
				<div className="flex gap-2 pt-2">
					<Button
						size="small"
						onClick={handleCancelChanges}
						variant="danger"
						disabled={!hasChanges}
						label="Clear"
						showIcon={false}
						style={{ flex: 1 }}
					/>
					<Button
						size="small"
						onClick={handleSaveChanges}
						disabled={!hasChanges}
						label="Save"
						showIcon={false}
						style={{ flex: 1 }}
					/>
				</div>
			)}
		</div>
	);
}
